// /managers/AuthManager.js

/*
 * ==============================
 * AuthManager.js - Firebase Auth Management with Integrated Settings
 * Manages authentication, user profiles, settings, claims, MCP servers, and global tools.
 *
 * CORRECTION: Fixed the global tools loading logic. It now correctly fetches the single 
 * document at `platformsettings/tools` instead of querying a non-existent `globalTools` collection.
 *
 * NEW: Implemented a universal, hierarchical tier access system.
 * - Added `TIER_HIERARCHY` to define the rank of each tier.
 * - Added the `hasTierAccess(requiredTier)` method to check permissions universally.
 * This prevents the need for hardcoded tier arrays in components.
 * ==============================
 */

import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp, collection, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { app } from '@/firebase/config';
import { CryptoUtils } from '@/utils/cryptoUtils';
import { getApiRootUrl } from '@/utils/setProdOrDev';
import { DEFAULT_PROMPTED_BOT_ID } from '@/utils/defaultBotProfile';

const AUTH_CONSTANTS = {
  SETUP_TIMEOUT: 15000,
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
};

// NEW: Exportable tier constants for app-wide consistency.
export const TIERS = Object.freeze({
  FREE: 'Free',
  PLUS: 'Plus',
  PRO: 'Pro',
  EVOK: 'Evok',
  EVOK_ULTRA: 'EvokUltra',
  BETA: 'Beta',
  // Internal/dev tiers
  ADMIN: 'Admin',
  E2EE: 'E2EE',
  PRO_SECURE: 'Pro Secure',
  ENTERPRISE: 'Enterprise' // Added for completeness
});

// NEW: Universal Tier Hierarchy for Access Control
// Higher numbers represent higher access levels.
export const TIER_HIERARCHY = Object.freeze({
  [TIERS.FREE]: 0,
  [TIERS.PLUS]: 1,
  [TIERS.PRO]: 2,
  [TIERS.BETA]: 2, // Beta is equivalent to Pro for feature access
  [TIERS.EVOK]: 3,
  [TIERS.PRO_SECURE]: 3, // Pro Secure is equivalent to Evok
  [TIERS.EVOK_ULTRA]: 4,
  [TIERS.ENTERPRISE]: 5,
  [TIERS.ADMIN]: 99,
  // Tiers not listed (like E2EE) will have a rank of -1 and fail most checks.
});

// Settings defaults
const TAILWIND_SIZES = {
  'text-xs': '0.75rem',
  'text-sm': '0.8125rem',
  'text-md': '0.875rem',
  'text-lg': '0.9375rem',
  'text-xl': '1rem',
  'text-[0.7rem]': '0.7rem'
};

export const FONT_SIZES = {
  xs: 'Extra Small',
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  xl: 'Extra Large'
};

export const FONT_SIZE_CLASSES = {
  chat: {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-md',
    lg: 'text-lg',
    xl: 'text-xl'
  },
  code: {
    xs: 'text-[0.7rem]',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-md',
    xl: 'text-xl'
  }
};

const DEFAULT_THEME_ID = '8';

const DEFAULT_Z_PREFERENCES = {
  chatFontSize: 'md',
  codeFontSize: 'md',
  codeLayout: 'side',
  notifications: {
    sound: false,
    desktop: false
  },
  instructions: {
    global: {
      enabled: false,
      content: ''
    }
  },
  botSwitchBehavior: {
    openInNewTab: true
  },
  pinnedBots: [DEFAULT_PROMPTED_BOT_ID],
  onboardingFinished: false, // NEW: Tracks if the user has dismissed the initial control stack guide.
  /* ========================
   * toolPreferences - NEW: Stores user's global tool & MCP server choices.
   * `null` indicates user hasn't made a choice; system defaults apply.
   * An array indicates an explicit user selection has been made.
   * ======================== */
  toolPreferences: {
    activeToolIds: null,
    activeServerIds: null
  },
  botModelPreferences: {
    lastUsedBot: null,
    lastUsedModelPerBot: {},
    modelPreferences: {}
  },
  userMeta: {
    displayName: '',
    techStack: [],
    environment: {
      os: '',
      ide: ''
    },
    additionalContext: '',
    instructions: {
      enabled: false,
      content: ''
    },
    usePersonalMemory: false,
  },
  useDevSettings: false,
  theme: DEFAULT_THEME_ID,
  useStreaming: true,
};

const DEFAULT_SETTINGS_STATE = {
  zPreferences: { ...DEFAULT_Z_PREFERENCES },
  useTools: true,
  lastUpdated: null,
};

const stripUndefined = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => stripUndefined(item)).filter(item => item !== undefined);
  }
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === undefined) return acc;
    if (value === null) {
      acc[key] = null;
      return acc;
    }
    if (typeof value === 'object') {
      const cleaned = stripUndefined(value);
      if (Array.isArray(value)) {
        acc[key] = cleaned;
      } else if (Object.keys(cleaned).length > 0 || Object.keys(value).length === 0) {
        acc[key] = cleaned;
      }
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

const deepMerge = (target, source) => {
  if (!source) return target;
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const targetValue = target[key];
      const sourceValue = source[key];

      if (sourceValue !== null && typeof sourceValue === 'object' && !Array.isArray(sourceValue) && targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
        result[key] = deepMerge(targetValue, sourceValue);
      } else {
        result[key] = sourceValue;
      }
    }
  }
  return result;
};


const transformFromDB = (dbData, currentTheme = DEFAULT_THEME_ID) => {
  const defaults = JSON.parse(JSON.stringify(DEFAULT_SETTINGS_STATE));
  const mergedSettings = deepMerge(defaults, dbData || {});
  mergedSettings.zPreferences.theme = dbData?.zPreferences?.theme || currentTheme;
  delete mergedSettings.zPreferences?.personalMemoryKey;
  delete (mergedSettings).mcpActive;

  return stripUndefined(mergedSettings);
};

const transformToDB = (currentSettings, theme) => {
  const defaults = JSON.parse(JSON.stringify(DEFAULT_SETTINGS_STATE));
  const settingsToSave = deepMerge(defaults, currentSettings);
  settingsToSave.zPreferences.theme = theme;
  settingsToSave.lastUpdated = Date.now();
  delete settingsToSave.zPreferences.personalMemoryKey;

  return stripUndefined(settingsToSave);
};

class AuthManagerClass {
  constructor() {
    this.auth = getAuth(app);
    this.googleProvider = new GoogleAuthProvider();
    this.functions = getFunctions(app);
    this.firestore = getFirestore(app);
    this._subscribers = new Set();
    this._processingAuth = false;
    this._cachedClaims = null;
    this._isSigningOut = false;
    this._saltCache = new Map();
    this._currentTheme = DEFAULT_THEME_ID;
    this._debouncedSaveTimeout = null;
    this._state = {
      user: null,
      claims: null,
      tier: 'loading',
      isInitialized: false,
      isAuthenticating: false,
      settings: { ...DEFAULT_SETTINGS_STATE },
      mcpServers: [],
      globalTools: [],
      settingsLoaded: false,
      mcpServersLoaded: false,
      globalToolsLoaded: false
    };

    this._loadGlobalTools();
    this._initSubscriptionSystem();
  }

 // ========== THIS IS THE CORRECTED FUNCTION ==========
 async _loadGlobalTools() {
  try {
      // Fetch the single document `tools` from the `platformsettings` collection.
      const toolsDocRef = doc(this.firestore, 'platformsettings', 'tools');
      const docSnap = await getDoc(toolsDocRef);

      if (docSnap.exists()) {
          const allToolsFromDB = docSnap.data().tools || [];
          const VALID_TIERS = new Set(["Free", "Plus", "Pro", "Evok", "EvokUltra", "Beta"]);

          // Filter tools based on the new, stricter requirement.
          const filteredTools = allToolsFromDB.filter(tool => {
              // A tool is only considered valid if it has a `tiers` array
              return Array.isArray(tool.tiers) && 
                     // and that array contains at least one of the recognized tiers.
                     tool.tiers.some(tier => VALID_TIERS.has(tier));
          });

          this._updateState({ globalTools: filteredTools, globalToolsLoaded: true });
      } else {
          console.warn("[AuthManager] The 'platformsettings/tools' document does not exist.");
          this._updateState({ globalTools: [], globalToolsLoaded: true });
      }
  } catch (error) {
      console.error("[AuthManager] Failed to load global tools:", error);
      this._updateState({ globalToolsLoaded: true }); // Mark as loaded to unblock UI
  }
}
  // ======================================================

  _initSubscriptionSystem() {
    onAuthStateChanged(this.auth, async (user) => {
        if (this._isSigningOut) {
            return;
        }

        if (!user) {
            // This handles cases where the user is signed out from another tab or token expires.
            // A specific error context isn't needed; the action is what matters.
            if (this._state.user) { // Only log if we thought a user was logged in.
                 console.warn('[AuthManager] onAuthStateChanged reported null user. Forcing local sign-out.');
            }
            return this._handleSignOut(new Error('onAuthStateChanged reported null user'));
        }

        // If we already have this user, it's likely a token refresh, not a new login.
        // We can skip the full re-authentication dance to prevent UI flicker.
        if (this._state.user && this._state.user.uid === user.uid && this._state.isInitialized) {
            return; 
        }

        this._updateState({ isAuthenticating: true });

        try {
            const idTokenResult = await user.getIdTokenResult(true);

            await this.ensureUserSalt(user.uid);

            const userProfile = await this.loadUserProfile(user.uid);
            if (!userProfile) {
                throw new Error(`User profile not found for uid: ${user.uid}. A profile must exist to complete login.`);
            }

            const settings = this._processUserSettings(userProfile);
            const mcpServers = await this._loadMcpServers(user.uid);

            let resolvedTier = 'Free';
            // Use the TIER_HIERARCHY keys as the list of all valid tiers now.
            const recognizedTiers = Object.keys(TIER_HIERARCHY); 
            if (userProfile.tier && recognizedTiers.includes(userProfile.tier)) {
                resolvedTier = userProfile.tier;
            } else if (idTokenResult.claims?.tier && recognizedTiers.includes(idTokenResult.claims.tier)) {
                resolvedTier = idTokenResult.claims.tier;
            }

            this._updateState({
                user,
                claims: idTokenResult.claims,
                tier: resolvedTier,
                settings,
                mcpServers,
                isInitialized: true,
                isAuthenticating: false,
                settingsLoaded: true,
                mcpServersLoaded: true,
            });

        } catch (error) {
            console.error(`[AuthManager] CRITICAL: Post-login setup failed for ${user.uid}, forcing sign-out.`, error);
            await this.signOut(error); // This will clear state and sign out from firebase
        }
    });
  }

  _processUserSettings(userProfile) {
    if (!userProfile) {
      return { ...DEFAULT_SETTINGS_STATE };
    }
    return transformFromDB(userProfile, this._currentTheme);
  }

  async _loadMcpServers(userId) {
    try {
      const mcpServersCollection = collection(this.firestore, 'users', userId, 'mcpServers');
      const snapshot = await getDocs(mcpServersCollection);
      return snapshot.docs.map(sDoc => ({
        id: sDoc.id,
        ...sDoc.data()
      }));
    } catch (error) {
      console.warn(`[AuthManager] Failed to load MCP servers for user ${userId}:`, error);
      return [];
    }
  }

  setCurrentTheme(theme) {
    this._currentTheme = theme;
    if (this._state.settingsLoaded && this._state.user) {
        this.loadUserProfile(this._state.user.uid).then(userProfile => {
            const newSettings = this._processUserSettings(userProfile);
            this._updateState({ settings: newSettings });
        }).catch(err => console.error("[AuthManager] Error reloading profile for theme change:", err));
    }
  }

  async _withTimeout(promise, timeout, message) {
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        const timer = setTimeout(() => {
          reject(new Error(message));
        }, timeout);
        promise.then(() => clearTimeout(timer)).catch(() => clearTimeout(timer));
      })
    ]);
  }

  _updateState(newState) {
    const oldState = { ...this._state };
    this._state = { ...this._state, ...newState };
    this._cachedClaims = newState.claims ?? this._cachedClaims;

    if (JSON.stringify(oldState) !== JSON.stringify(this._state)) {
      this._notifySubscribers();
    }
  }

  _notifySubscribers() {
    this._subscribers.forEach(callback => callback(this._state));
  }

  async _handleSignOut(error = null) {
    console.groupCollapsed('%c[AuthManager] Sign-Out Process Triggered', 'color: #FF4500; font-weight: bold;');
    if (error) {
      console.error('Sign-out was triggered by a specific event or error:', error);
      if (error instanceof Error && error.stack) {
          console.error('Stack trace:', error.stack);
      }
    } else {
      console.log('Sign-out initiated without a specific context (e.g., user clicked logout button).');
    }
    console.trace('Call stack for this sign-out:');
    console.groupEnd();

    if (this._isSigningOut) {
      console.warn('[AuthManager] Sign-out process is already running. Ignoring redundant call.');
      return;
    }

    this._isSigningOut = true;
    try {
      try {
        CryptoUtils.clearKeys();
        this._clearSaltCache();
      } catch (e) {
        console.warn('[AuthManager] Error clearing crypto keys/salt cache during sign out:', e);
      }

      if (this.auth.currentUser) {
        await firebaseSignOut(this.auth);
      }

      this._updateState({
        user: null,
        claims: null,
        tier: 'anonymous',
        isInitialized: true,
        isAuthenticating: false,
        settings: { ...DEFAULT_SETTINGS_STATE },
        mcpServers: [],
        settingsLoaded: false,
        mcpServersLoaded: false
      });
    } catch (e) {
      console.error('[AuthManager] Error during Firebase signOut operation:', e);
      this._updateState({
        user: null,
        claims: null,
        tier: 'anonymous',
        isInitialized: true,
        isAuthenticating: false,
        settings: { ...DEFAULT_SETTINGS_STATE },
        mcpServers: [],
        settingsLoaded: false,
        mcpServersLoaded: false
      });
    } finally {
      this._isSigningOut = false;
    }
  }

  subscribe(callback) {
    this._subscribers.add(callback);
    callback(this._state);
    return () => this._subscribers.delete(callback);
  }

  getState() {
    return { ...this._state };
  }

  getSettings() {
    return this._state.settings;
  }

  getMcpServers() {
    return this._state.mcpServers;
  }
  
  getGlobalTools() {
    return this._state.globalTools;
  }

  async updateSettings(updates) {
    if (!this._state.user) {
      console.warn("[AuthManager] Attempted to update settings with no user authenticated.");
      return;
    }

    const newSettings = deepMerge(this._state.settings, updates);

    if (JSON.stringify(newSettings) !== JSON.stringify(this._state.settings)) {
      this._updateState({ settings: newSettings });

      if (this._debouncedSaveTimeout) {
        clearTimeout(this._debouncedSaveTimeout);
      }
      this._debouncedSaveTimeout = setTimeout(async () => {
        try {
          const payload = transformToDB(newSettings, this._currentTheme);
          await this.saveUserProfile(payload);
        } catch (error) {
          console.error("[AuthManager] Failed to save settings to DB:", error);
        }
      }, 1000);
    }
  }

  updateUserMeta(metaUpdates) {
    return this.updateSettings({ zPreferences: { userMeta: metaUpdates } });
  }

  setLastUsedBot(botId) {
    return this.updateSettings({ zPreferences: { botModelPreferences: { lastUsedBot: botId } } });
  }

  setLastUsedModelForBot(botId, modelId) {
    return this.updateSettings({ zPreferences: { botModelPreferences: { lastUsedModelPerBot: { [botId]: modelId } } } });
  }

  getPreferredModelForBot(botId) {
    const prefs = this._state.settings.zPreferences?.botModelPreferences;
    if (!prefs) return null;
    const botPrefs = prefs.modelPreferences?.[botId];
    if (botPrefs?.defaultModel) return botPrefs.defaultModel;
    if (prefs.lastUsedModelPerBot?.[botId]) return prefs.lastUsedModelPerBot[botId];
    return null;
  }

  setDefaultModelForBot(botId, modelId) {
    return this.updateSettings({
      zPreferences: {
        botModelPreferences: {
          modelPreferences: {
            [botId]: {
              defaultModel: modelId
            }
          }
        }
      }
    });
  }

  starModel(botId, modelId, starred = true) {
    const currentModelPrefs = this._state.settings.zPreferences?.botModelPreferences?.modelPreferences || {};
    const currentBotPrefs = currentModelPrefs[botId] || {};
    const currentStarred = currentBotPrefs.starredModels || [];

    let newStarred;
    if (starred) {
      newStarred = [...new Set([...currentStarred, modelId])];
    } else {
      newStarred = currentStarred.filter(id => id !== modelId);
    }

    return this.updateSettings({
      zPreferences: {
        botModelPreferences: {
          modelPreferences: {
            [botId]: {
              ...currentBotPrefs,
              starredModels: newStarred
            }
          }
        }
      }
    });
  }

  hideModel(botId, modelId, hidden = true) {
    const currentModelPrefs = this._state.settings.zPreferences?.botModelPreferences?.modelPreferences || {};
    const currentBotPrefs = currentModelPrefs[botId] || {};
    const currentHidden = currentBotPrefs.hiddenModels || [];

    let newHidden;
    if (hidden) {
      newHidden = [...new Set([...currentHidden, modelId])];
    } else {
      newHidden = currentHidden.filter(id => id !== modelId);
    }

    return this.updateSettings({
      zPreferences: {
        botModelPreferences: {
          modelPreferences: {
            [botId]: {
              ...currentBotPrefs,
              hiddenModels: newHidden
            }
          }
        }
      }
    });
  }

  getModelPreferencesForBot(botId) {
    const prefs = this._state.settings.zPreferences?.botModelPreferences?.modelPreferences?.[botId] || {};
    return {
      starredModels: prefs.starredModels || [],
      hiddenModels: prefs.hiddenModels || [],
      defaultModel: prefs.defaultModel || null
    };
  }

  setThinkingPreference(botId, modelId, enabled, budget = 1800) {
    const currentModelPrefs = this._state.settings.zPreferences?.botModelPreferences?.modelPreferences || {};
    const currentBotPrefs = currentModelPrefs[botId] || {};
    const currentThinkingPrefs = currentBotPrefs.thinkingPreferences || {};
  
    return this.updateSettings({
      zPreferences: {
        botModelPreferences: {
          modelPreferences: {
            [botId]: {
              ...currentBotPrefs,
              thinkingPreferences: {
                ...currentThinkingPrefs,
                [modelId]: { enabled, budget }
              }
            }
          }
        }
      }
    });
  }
  
  getThinkingPreferences(botId, modelId) {
    const prefs = this._state.settings.zPreferences?.botModelPreferences?.modelPreferences?.[botId]?.thinkingPreferences?.[modelId];
    return prefs || null;
  }

  async addMcpServer(serverData) {
    if (!this._state.user) {
      throw new Error("User not authenticated to add MCP server.");
    }

    try {
      const mcpServersCollection = collection(this.firestore, 'users', this._state.user.uid, 'mcpServers');
      const serverDocRef = serverData.id && !serverData.isRecommendation ?
        doc(mcpServersCollection, serverData.id) :
        doc(mcpServersCollection);

      const serverPayload = { ...serverData };
      if (serverPayload.id && (serverPayload.isRecommendation || !this._state.mcpServers.find(s => s.id === serverPayload.id))) {
        delete serverPayload.id;
      }
      delete serverPayload.isRecommendation;

      await setDoc(serverDocRef, serverPayload, { merge: true });

      const newServer = { id: serverDocRef.id, ...serverPayload };
      const existingServerIndex = this._state.mcpServers.findIndex(s => s.id === newServer.id);
      let updatedServers;
      if (existingServerIndex > -1) {
        updatedServers = [...this._state.mcpServers];
        updatedServers[existingServerIndex] = newServer;
      } else {
        updatedServers = [...this._state.mcpServers, newServer];
      }

      this._updateState({ mcpServers: updatedServers });
      return newServer;
    } catch (error) {
      console.error("[AuthManager] Error adding MCP server:", error);
      throw error;
    }
  }

  async updateMcpServer(serverId, updates) {
    if (!this._state.user) {
      throw new Error("User not authenticated to update MCP server.");
    }

    try {
      const serverDocRef = doc(this.firestore, 'users', this._state.user.uid, 'mcpServers', serverId);
      await updateDoc(serverDocRef, updates);

      const updatedServers = this._state.mcpServers.map(s =>
        s.id === serverId ? { ...s, ...updates } : s
      );

      this._updateState({ mcpServers: updatedServers });
      return true;
    } catch (error) {
      console.error(`[AuthManager] Error updating MCP server ${serverId}:`, error);
      throw error;
    }
  }

  async deleteMcpServer(serverId) {
    if (!this._state.user) {
      throw new Error("User not authenticated to delete MCP server.");
    }

    try {
      const serverDocRef = doc(this.firestore, 'users', this._state.user.uid, 'mcpServers', serverId);
      await deleteDoc(serverDocRef);

      const updatedServers = this._state.mcpServers.filter(s => s.id !== serverId);
      this._updateState({ mcpServers: updatedServers });
      return true;
    } catch (error) {
      console.error(`[AuthManager] Error deleting MCP server ${serverId}:`, error);
      throw error;
    }
  }

  getFontClass(type, size) {
    return FONT_SIZE_CLASSES[type]?.[size] || FONT_SIZE_CLASSES[type]?.md;
  }

  getCodeFontSize(size) {
    const tailwindClass = FONT_SIZE_CLASSES.code[size] || FONT_SIZE_CLASSES.code.md;
    return TAILWIND_SIZES[tailwindClass] || TAILWIND_SIZES['text-md'];
  }

     getCurrentUser() {
      const cachedUser = this._state.user;
      if (cachedUser) {
        return {
          ...cachedUser,
          getIdToken: async (forceRefresh = false) => {
            if (this._isProcessingSummary) {
              console.warn('🔐 AuthManager: Preventing token refresh during summary operation');
              return cachedUser.accessToken || this.auth.currentUser?.accessToken;
            }
            return this.auth.currentUser?.getIdToken(forceRefresh);
          }
        };
      }
      return this.auth.currentUser;
    }

  async _forceKeySync(userId) {
    try {
      const keysDoc = await getDoc(doc(this.firestore, `users/${userId}/secure/keys`));
      return keysDoc.exists();
    } catch (error) {
      console.warn(`[AuthManager] Error checking keys document for user ${userId}:`, error);
      return false;
    }
  }

  async signInWithEmail(email, password) {
    this._updateState({ isAuthenticating: true });
    try {
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      return result.user;
    } catch (error) {
      this._updateState({ isAuthenticating: false });
      throw error;
    }
  }

  async signUpWithEmail(email, password) {
    this._updateState({ isAuthenticating: true });
    try {
      const result = await createUserWithEmailAndPassword(this.auth, email, password);
      return result.user;
    } catch (error) {
      this._updateState({ isAuthenticating: false });
      throw error;
    }
  }

  async signInWithGoogle() {
    this._updateState({ isAuthenticating: true });
    try {
      const result = await signInWithPopup(this.auth, this.googleProvider);
      return result;
    } catch (error) {
      this._updateState({ isAuthenticating: false });
      throw error;
    }
  }

  async createUserProfile(profileData) {
    const user = this.getCurrentUser();
    if (!user) throw new Error('No user found to create profile for.');

    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${getApiRootUrl()}/user-init/initialize-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create user profile: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        await this.loadUserProfile(user.uid).then(profile => {
            if (profile) {
                const newSettings = this._processUserSettings(profile);
                this._updateState({ settings: newSettings, settingsLoaded: true });
            }
        });
      }
      return result;
    } catch (error) {
      console.error("[AuthManager] Error creating user profile via Cloud Run:", error);
      throw error;
    }
  }

  async signOut(error = null) {
    return this._handleSignOut(error);
  }

  _getCachedSalt(userId) {
    return this._saltCache.get(userId);
  }

  _setSaltCache(userId, salt) {
    this._saltCache.set(userId, salt);
  }

  _clearSaltCache() {
    this._saltCache.clear();
  }

  async ensureUserSalt(userId) {
    const cachedSalt = this._getCachedSalt(userId);
    if (cachedSalt && CryptoUtils.isInitialized()) {
      return { encryptionSalt: cachedSalt };
    }

    let attempts = 0;
    let lastError;

    while (attempts < AUTH_CONSTANTS.MAX_RETRIES) {
      attempts++;
      try {
        let userProfile = await this.loadUserProfile(userId);

        if (!userProfile?.encryptionSalt) {
          const user = this.getCurrentUser();
          if (!user || user.uid !== userId) throw new Error('User mismatch or not authenticated for salt initialization');

          const idToken = await user.getIdToken();
          const response = await this._withTimeout(
            fetch(`${getApiRootUrl()}/user-init/initialize-user`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify({ uid: userId })
            }),
            AUTH_CONSTANTS.SETUP_TIMEOUT,
            'User salt initialization via Cloud Run timeout'
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Cloud Run salt initialization failed: ${response.status} - ${errorText}`);
          }

          const result = await response.json();
          if (!result?.success || !result?.profile?.encryptionSalt) {
            throw new Error('Failed to initialize user or retrieve salt from Cloud Run.');
          }
          userProfile = result.profile;
        }

        if (!userProfile?.encryptionSalt) {
          throw new Error('Failed to verify user salt even after initialization attempt.');
        }

        this._setSaltCache(userId, userProfile.encryptionSalt);

        if (!CryptoUtils.isInitialized()) {
          await CryptoUtils.initializeEncryption(userId, userProfile.encryptionSalt);
        }
        return userProfile;

      } catch (error) {
        console.warn(`[AuthManager] ensureUserSalt attempt ${attempts} failed for user ${userId}:`, error);
        lastError = error;
        if (attempts < AUTH_CONSTANTS.MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, AUTH_CONSTANTS.RETRY_DELAY));
        }
      }
    }
    console.error(`[AuthManager] All attempts to ensure user salt failed for user ${userId}. Last error:`, lastError);
    throw lastError;
  }

  async loadUserProfile(userId) {
    if (!userId) return null;
    try {
      const profileDoc = await getDoc(doc(this.firestore, 'users', userId));
      if (profileDoc.exists()) {
        return profileDoc.data();
      }
      console.warn(`[AuthManager] No user profile found for UID: ${userId}`);
      return null;
    } catch (error) {
      console.error(`[AuthManager] Error loading user profile for UID ${userId}:`, error);
      throw error;
    }
  }

  async saveUserProfile(profileData) {
    const user = this.getCurrentUser();
    if (!user) throw new Error('No user found to save profile for.');

    try {
      const dataToSave = {
        ...profileData,
        updatedAt: serverTimestamp(),
        uid: user.uid,
      };
      
      if (user.email && !dataToSave.email) {
        dataToSave.email = user.email;
      }

      await setDoc(doc(this.firestore, 'users', user.uid), dataToSave, { merge: true });

      if (profileData.tier && profileData.tier !== this._state.tier) {
        this._updateState({ tier: profileData.tier });
      }
      return true;

    } catch (error) {
      console.error(`[AuthManager] Error saving user profile for UID ${user.uid}:`, error);
      throw error;
    }
  }

  async updateDisplayName(newDisplayName) {
    const user = this.getCurrentUser();
    if (!user) throw new Error('No user found to update display name.');
    if (!newDisplayName || typeof newDisplayName !== 'string' || newDisplayName.trim().length === 0) {
        throw new Error('Invalid display name provided.');
    }

    try {
      await updateProfile(user, { displayName: newDisplayName });

      const profileUpdates = {
        displayName: newDisplayName,
        updatedAt: serverTimestamp()
      };
      await setDoc(doc(this.firestore, 'users', user.uid), profileUpdates, { merge: true });

      const updatedUser = { ...this._state.user, displayName: newDisplayName };
      const updatedSettings = {
        ...this._state.settings,
        zPreferences: {
          ...this._state.settings.zPreferences,
          userMeta: {
            ...this._state.settings.zPreferences.userMeta,
            displayName: newDisplayName
          }
        }
      };
      this._updateState({
        user: updatedUser,
        settings: updatedSettings
      });

      return true;
    } catch (error) {
      console.error(`[AuthManager] Error updating display name for UID ${user.uid}:`, error);
      throw error;
    }
  }

  getDisplayName() {
    return this._state.settings?.zPreferences?.userMeta?.displayName ||
           this._state.user?.displayName ||
           this.auth.currentUser?.displayName ||
           null;
  }

  async updateProfile(profileUpdates) {
    return this.saveUserProfile(profileUpdates);
  }

  async loadPreferences() {
    return this._state.settings.zPreferences || { ...DEFAULT_Z_PREFERENCES };
  }

  async savePreferences(preferences) {
    return this.updateSettings({ zPreferences: preferences });
  }

  async resetPassword(email) {
    if (!email || typeof email !== 'string') {
        throw new Error("Valid email is required for password reset.");
    }
    try {
      await sendPasswordResetEmail(this.auth, email);
      return true;
    } catch (error) {
      console.error("[AuthManager] Error sending password reset email:", error);
      throw error;
    }
  }

  getTier() {
    return this._state.tier;
  }

  /**
   * NEW: Checks if the current user's tier meets or exceeds the required tier level.
   * This is the universal method for feature gating.
   * @param {string} requiredTier - The minimum tier required for access (e.g., TIERS.PRO).
   * @returns {boolean} - True if the user has access, false otherwise.
   */
  hasTierAccess(requiredTier) {
    const currentUserTier = this._state.tier;
    const userRank = TIER_HIERARCHY[currentUserTier] ?? -1;
    const requiredRank = TIER_HIERARCHY[requiredTier] ?? 999;

    if (userRank === -1) {
      console.warn(`[AuthManager] Current user tier "${currentUserTier}" is not recognized in TIER_HIERARCHY.`);
    }

    return userRank >= requiredRank;
  }

  getClaims() {
    return this._state.claims ?? this._cachedClaims;
  }

  isInitialized() {
    return this._state.isInitialized;
  }

  areSettingsLoaded() {
    return this._state.settingsLoaded;
  }

  areMcpServersLoaded() {
    return this._state.mcpServersLoaded;
  }

  areGlobalToolsLoaded() {
    return this._state.globalToolsLoaded;
  }

  cleanup() {
    if (this._debouncedSaveTimeout) {
      clearTimeout(this._debouncedSaveTimeout);
      this._debouncedSaveTimeout = null;
    }
  }
}

export const authManager = new AuthManagerClass();
export { DEFAULT_Z_PREFERENCES };
